import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { useAsync } from '../hooks/useAsync';
import { ErrorMessage, Skeleton, useToast } from '../components';

/** Optional AI drafting: key entry, model choice and a clear statement of what leaves the device. */
export function AiCard() {
  const toast = useToast();
  const [state, reload] = useAsync(api.aiStatus);
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      if (done) toast(done);
      reload();
    } catch (e) {
      setError(e instanceof ApiError ? (e.fields.key ?? e.message) : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const saveKey = (e: FormEvent) => {
    e.preventDefault();
    if (!key.trim()) {
      setError('Paste your Anthropic API key first.');
      return;
    }
    void run(async () => {
      await api.aiConfig({ key: key.trim() });
      setKey(''); // never keep it in the page after saving
    }, 'Key saved');
  };

  return (
    <section className="card" aria-labelledby="ai-title">
      <h2 className="card-title" id="ai-title">
        AI recipe drafting (optional)
      </h2>
      <p className="card-sub">
        Describe a drink and Muddle searches the web for recipes. Only the words you type are sent
        to Anthropic. Your recipes and ratings never leave this device, and nothing is saved until
        you approve it.
      </p>
      {state.status === 'loading' && <Skeleton height={80} />}
      {state.status === 'error' && <ErrorMessage message={state.message} onRetry={reload} />}
      {state.status === 'ready' && (
        <>
          <div className="list-row">
            <span>Status</span>
            <span>
              {state.data.keySource === 'env'
                ? 'On (key from the server environment)'
                : state.data.configured
                  ? 'On (key saved, encrypted on this device)'
                  : 'Off'}
            </span>
          </div>

          {!state.data.configured && state.data.canStoreKey && (
            <form onSubmit={saveKey} noValidate>
              <div className="field">
                <label htmlFor="ai-key">Anthropic API key</label>
                <input
                  id="ai-key"
                  type="password"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="sk-ant-…"
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  aria-invalid={!!error}
                />
                {error && (
                  <div className="field-error" role="alert">
                    {error}
                  </div>
                )}
              </div>
              <p className="card-sub">
                Create one at console.anthropic.com. A key with a low monthly spend limit is a good
                idea: a search costs a few cents.
              </p>
              <div className="actions">
                <button className="btn btn-primary" type="submit" disabled={busy} aria-busy={busy}>
                  {busy ? 'Saving…' : 'Save key'}
                </button>
              </div>
            </form>
          )}

          {!state.data.configured && !state.data.canStoreKey && (
            <p className="card-sub">
              This server reads the key from the <code>ANTHROPIC_API_KEY</code> environment
              variable, so it is never stored by Muddle. Set it and restart to turn this on (see
              DEPLOY.md).
            </p>
          )}

          {state.data.configured && (
            <>
              <div className="field">
                <label htmlFor="ai-model">Model</label>
                <select
                  id="ai-model"
                  value={state.data.model ?? ''}
                  disabled={busy}
                  onChange={(e) =>
                    void run(() => api.aiConfig({ model: e.target.value }), 'Model saved')
                  }
                >
                  {state.data.models.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label} · {m.note}
                    </option>
                  ))}
                </select>
              </div>
              {error && (
                <div className="msg err" role="alert">
                  {error}
                </div>
              )}
              {state.data.keySource === 'stored' && (
                <div className="actions">
                  <button
                    className="btn btn-ghost"
                    type="button"
                    disabled={busy}
                    onClick={() => void run(() => api.aiClearKey(), 'Key removed')}
                  >
                    Remove key
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}
